import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Input,
  Typography,
  Table,
  Tag,
  Button,
  Grid,
  List,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  SettingOutlined,
  DownOutlined,
  UpOutlined,
  RightOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";

import ResponseTimeModal from "../components/ResponseTimeModal";
import GroupAvatar from "../components/GroupAvatar";
import MemberPanel from "../components/MemberPanel";
import { groupOrdersApi as waitTimeService } from "../api/groupOrdersApi";
import { buildGroupListRequestKey } from "../model/types";
import type { Group, SellerGroup } from "../model/types";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { appNotification } from "../../../shared/notifications/appNotification";
import "../styles/WaitingTime.css";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export const WaitTimeList: React.FC = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [data, setData] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [expandedMobileIds, setExpandedMobileIds] = useState<string[]>([]);
  const [sellerGroups, setSellerGroups] = useState<SellerGroup[]>([]);
  const [selectedGroupSeller, setSelectedGroupSeller] = useState<SellerGroup>();
  const [originalGroupSellerId, setOriginalGroupSellerId] = useState<
    string | null
  >(null);
  const latestRequestIdRef = useRef(0);
  const lastAutomaticRequestKeyRef = useRef<string>();

  const currentPage = pagination.current ?? 1;
  const currentPageSize = pagination.pageSize ?? 10;

  const fetchGroups = useCallback(
    async (page = 1, pageSize = 10, search = "") => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setLoading(true);
      try {
        const response = await waitTimeService.fetchGroups({
          page,
          pageSize,
          search,
        });
        if (requestId !== latestRequestIdRef.current) return;
        setData(response.data);
        setTotal(response.total);
      } catch {
        if (requestId === latestRequestIdRef.current) {
          appNotification.error({ message: "データの取得に失敗しました。" });
        }
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (searchText === debouncedSearchText) return;
    const timeout = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setPagination((previous) =>
        previous.current === 1 ? previous : { ...previous, current: 1 },
      );
    }, 500);
    return () => clearTimeout(timeout);
  }, [debouncedSearchText, searchText]);

  useEffect(() => {
    const requestKey = buildGroupListRequestKey(
      currentPage,
      currentPageSize,
      debouncedSearchText,
    );
    if (lastAutomaticRequestKeyRef.current === requestKey) return;
    lastAutomaticRequestKeyRef.current = requestKey;
    void fetchGroups(currentPage, currentPageSize, debouncedSearchText);
  }, [currentPage, currentPageSize, debouncedSearchText, fetchGroups]);

  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: () => fetchGroups(currentPage, currentPageSize, debouncedSearchText) });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, fetchGroups, currentPage, currentPageSize, debouncedSearchText]);

  useEffect(() => {
    if (modalOpen && selectedGroup) {
      const fetchData = async () => {
        try {
          const response = await waitTimeService.fetchAvailableSellerGroups(
            selectedGroup.id,
          );
          const available = response;
          if (
            selectedGroupSeller?.id &&
            !available.some((s: SellerGroup) => s.id === selectedGroupSeller.id)
          ) {
            setSellerGroups([...available, selectedGroupSeller]);
          } else {
            setSellerGroups(available);
          }
        } catch {
          appNotification.error({ message: "データの取得に失敗しました。" });
        }
      };
      fetchData();
    }
  }, [modalOpen, selectedGroup, selectedGroupSeller]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    const nextPageSize = newPagination.pageSize ?? 10;
    setPagination((previous) => ({
      ...previous,
      current:
        nextPageSize !== previous.pageSize ? 1 : (newPagination.current ?? 1),
      pageSize: nextPageSize,
    }));
  };

  const handleSettingClick = async (group: Group) => {
    try {
      const sellerGroup = await waitTimeService.fetchSellerGroup(group.id);
      setSelectedGroupSeller(sellerGroup || undefined);
      setOriginalGroupSellerId(sellerGroup?.id ?? null);
      setSelectedGroup(group);
      setModalOpen(true);
    } catch {
      appNotification.error({
        message: "担当セリ人グループの取得に失敗しました。",
      });
    }
  };

  const handleSaveResponseTime = async (
    value: number,
    unit: "seconds" | "minutes" | "hours",
  ) => {
    if (!selectedGroup) return;
    try {
      if (selectedGroupSeller?.id) {
        await waitTimeService.updateGroupSeller(
          selectedGroup.id,
          selectedGroupSeller.id,
        );
      } else if (originalGroupSellerId) {
        await waitTimeService.updateGroupSeller(selectedGroup.id, null);
      }

      await waitTimeService.updateResponseTime(selectedGroup.id, {
        value,
        unit,
      });
      appNotification.success({ message: "設定を保存しました。" });
      setModalOpen(false);
      setOriginalGroupSellerId(null);
      void fetchGroups(currentPage, currentPageSize, debouncedSearchText);
    } catch (error) {
      appNotification.error({ message: "保存に失敗しました。" });
    }
  };

  const getResponseTimeLabel = (waitSeconds?: number) => {
    if (waitSeconds === undefined || waitSeconds === null) {
      return "15 分";
    }

    if (waitSeconds === 0) {
      return "0 秒";
    }

    if (waitSeconds % 3600 === 0 && waitSeconds > 0) {
      return `${waitSeconds / 3600} 時間`;
    }

    if (waitSeconds % 60 === 0 && waitSeconds > 0) {
      return `${waitSeconds / 60} 分`;
    }

    return `${waitSeconds} 秒`;
  };

  const toggleMobileExpand = (id: string) => {
    setExpandedMobileIds((prev) =>
      prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id],
    );
  };

  const columns: ColumnsType<Group> = [
    {
      title: "LINEグループ名",
      ellipsis: true,
      dataIndex: "name",
      key: "name",
      width: 300,
      fixed: screens.lg ? "left" : undefined,
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <GroupAvatar />
              <span style={{ fontSize: 16 }}>{text}</span>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "担当セリ人グループ",
      ellipsis: {
        showTitle: true,
      },
      dataIndex: "groupSellerName",
      key: "groupSellerName",
      width: 160,
      // fixed: screens.lg ? "left" : undefined,
      align: "right",
      render: (text: string) => (
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            justifyContent: "flex-end",
          }}
        >
          <Tooltip placement="topLeft" title={text}>
            <span style={{ fontSize: 16 }}>{text}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "メンバー",
      ellipsis: {
        showTitle: true,
      },
      dataIndex: "memberCount",
      key: "memberCount",
      align: "center",
      width: 100,
    },
    {
      title: "応答時間",
      ellipsis: {
        showTitle: true,
      },
      key: "responseTime",
      align: "center",
      width: 100,
      render: (_, record) => getResponseTimeLabel(record.waitSeconds),
    },
    {
      title: "ステータス",
      ellipsis: {
        showTitle: true,
      },
      key: "status",
      dataIndex: "status",
      align: "center",
      width: 100,
      // fixed: screens.lg ? "right" : undefined,
      render: (status: boolean) =>
        status ? (
          <Tag
            color="success"
            style={{ borderRadius: 12, padding: "2px 12px" }}
          >
            有効
          </Tag>
        ) : (
          <Tag
            color="default"
            style={{ borderRadius: 12, padding: "2px 12px" }}
          >
            無効
          </Tag>
        ),
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      width: 100,
      fixed: screens.lg ? "right" : undefined,
      render: (_, record) => (
        <Button
          onClick={() => handleSettingClick(record)}
          icon={<SettingOutlined />}
          style={{ borderRadius: 8 }}
        />
      ),
    },
  ];

  return (
    <div className="wait-time-page">
      <div className="wait-time-hero">
        <Title level={isMobile ? 3 : 1} style={{ margin: 0 }}>
          ボット応答時間管理
        </Title>

        <Text style={{ color: "#6B7280", fontSize: isMobile ? 12 : 14 }}>
          各LINE注文グループごとのボット自動応答までの待機時間を設定します。
        </Text>
      </div>

      <Card className="wait-time-list-card" bordered={false}>
        <div className="wait-time-toolbar">
          <Input
            size="large"
            prefix={<SearchOutlined style={{ color: "#9CA3AF" }} />}
            placeholder="グループチャットを検索..."
            style={{ width: isMobile ? "100%" : 400, borderRadius: 8 }}
            value={searchText}
            onChange={handleSearchChange}
          />
          {!isMobile && (
            <Button
              size="large"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() =>
                void fetchGroups(
                  currentPage,
                  currentPageSize,
                  debouncedSearchText,
                )
              }
            >
              更新
            </Button>
          )}
        </div>

        {isMobile ? (
          <>
            <List
              className="wait-time-mobile-list"
              loading={loading}
              dataSource={data}
              pagination={false}
              renderItem={(item) => {
                const expanded = expandedMobileIds.includes(item.id);
                return (
                  <List.Item className="wait-time-mobile-list-item">
                    <Card className="wait-time-mobile-card">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                          }}
                        >
                          <GroupAvatar />
                          <span style={{ fontSize: 16, fontWeight: 500 }}>
                            {item.name}
                          </span>
                        </div>
                        {item.status ? (
                          <Tag
                            color="success"
                            style={{ margin: 0, borderRadius: 12 }}
                          >
                            有効
                          </Tag>
                        ) : (
                          <Tag
                            color="default"
                            style={{ margin: 0, borderRadius: 12 }}
                          >
                            無効
                          </Tag>
                        )}
                      </div>

                      {item.groupSellerName && (
                        <div style={{ marginTop: 16, display: "flex", gap: 4 }}>
                          <span style={{ color: "#9ca3af", marginRight: 4 }}>
                            担当セリ人グループ:
                          </span>
                          <Tooltip
                            placement="topLeft"
                            title={item.groupSellerName}
                          >
                            <span
                              style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                maxWidth: 150,
                              }}
                            >
                              {item.groupSellerName}
                            </span>
                          </Tooltip>
                        </div>
                      )}

                      <div
                        style={{
                          marginTop: 16,
                          display: "flex",
                          gap: 16,
                          color: "#4b5563",
                          fontSize: 13,
                        }}
                      >
                        <div>
                          <span style={{ color: "#9ca3af", marginRight: 4 }}>
                            メンバー:
                          </span>
                          {item.memberCount}人
                        </div>
                        <div>
                          <span style={{ color: "#9ca3af", marginRight: 4 }}>
                            応答時間:
                          </span>
                          {getResponseTimeLabel(item.waitSeconds)}
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: 16,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          type="text"
                          size="small"
                          onClick={() => toggleMobileExpand(item.id)}
                          style={{ color: "#1890ff", padding: 0 }}
                        >
                          {expanded ? (
                            <>
                              メンバーを隠す <UpOutlined />
                            </>
                          ) : (
                            <>
                              メンバーを見る <DownOutlined />
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleSettingClick(item)}
                          icon={<SettingOutlined />}
                          size="middle"
                          style={{ borderRadius: 8 }}
                        >
                          設定
                        </Button>
                      </div>

                      {expanded && (
                        <div style={{ marginTop: 16 }}>
                          <MemberPanel members={item.members} />
                        </div>
                      )}
                    </Card>
                  </List.Item>
                );
              }}
            />
            <MobilePagination
              current={currentPage}
              pageSize={currentPageSize}
              total={total}
              onChange={(current) =>
                setPagination((previous) => ({ ...previous, current }))
              }
            />
          </>
        ) : (
          <Table<Group>
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            size="middle"
            pagination={{
              current: currentPage,
              pageSize: currentPageSize,
              total,
              size: "middle",
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (count, range) =>
                `${count} 件中 ${range[0]}-${range[1]} を表示`,
              position: ["bottomRight"],
            }}
            onChange={handleTableChange}
            expandable={{
              expandedRowRender: (record) => (
                <MemberPanel members={record.members} />
              ),
              expandIconColumnIndex: 0,
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <DownOutlined
                    onClick={(e) => onExpand(record, e)}
                    style={{
                      marginRight: 16,
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                  />
                ) : (
                  <RightOutlined
                    onClick={(e) => onExpand(record, e)}
                    style={{
                      marginRight: 16,
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                  />
                ),
            }}
            scroll={{ x: 1080 }}
          />
        )}
      </Card>

      <ResponseTimeModal
        open={modalOpen}
        group={selectedGroup}
        selectedGroupSeller={selectedGroupSeller}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveResponseTime}
        sellerGroups={sellerGroups}
        setSelectedGroupSeller={setSelectedGroupSeller}
      />
    </div>
  );
};
